import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  Download,
  Filter,
  Mail,
  MoreHorizontal,
  Search,
  Send,
  X,
  FileText,
  Image as ImageIcon,
  File,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { toast } from "sonner";
import { ApiResponse } from "../types/common";
import { queriesRelatedApi } from "../utils/api";

interface QueryAttachment {
  id: string;
  name: string;
  size: string;
  type: "image" | "document" | "other";
  url: string;
}

interface UserQuery {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  sentReply: boolean;
  dateCreated: string;
  attachments?: QueryAttachment[];
}

// const mockQueries: UserQuery[] = [
//   {
//     id: "1",
//     name: "Bernard Chevis",
//     email: "Bchevis@duncanvillesd.org",
//     subject: "canceling",
//     message:
//       "Good morning.<br>I would like to cancel this service. I haven't been able to use it.<br><br>",
//     sentReply: false,
//     dateCreated: "2023-09-28T12:00:21.632Z",
//     attachments: [
//       {
//         id: "att1",
//         name: "cancellation_request.pdf",
//         size: "245 KB",
//         type: "document",
//         url: "/downloads/cancellation_request.pdf",
//       },
//     ],
//   },
//   {
//     id: "2",
//     name: "Gary Coleman",
//     email: "Lynn.coleman@gmail.com",
//     subject: "checking flow",
//     message: "With <b>coroutine&nbsp;</b><br>",
//     sentReply: false,
//     dateCreated: "2023-09-27T15:30:45.123Z",
//   },
//   {
//     id: "3",
//     name: "Gary Coleman",
//     email: "Lynn.coleman@gmail.com",
//     subject: "Ninja",
//     message: "Ninja Storm turtle warrior&nbsp;",
//     sentReply: false,
//     dateCreated: "2023-09-26T09:15:22.456Z",
//     attachments: [
//       {
//         id: "att2",
//         name: "ninja_screenshot.png",
//         size: "1.2 MB",
//         type: "image",
//         url: "/downloads/ninja_screenshot.png",
//       },
//       {
//         id: "att3",
//         name: "feedback_notes.docx",
//         size: "85 KB",
//         type: "document",
//         url: "/downloads/feedback_notes.docx",
//       },
//     ],
//   },
//   {
//     id: "4",
//     name: "Trent Loseke",
//     email: "tnloseke@yahoo.com",
//     subject: "feedback",
//     message: "I have some suggestions for improvement&nbsp;",
//     sentReply: false,
//     dateCreated: "2023-09-25T14:20:33.789Z",
//   },
//   {
//     id: "5",
//     name: "Trey Beasley",
//     email: "Trey.beasley@hi5mg.com",
//     subject: "my sub",
//     message: "my message",
//     sentReply: false,
//     dateCreated: "2023-09-24T11:45:12.345Z",
//   },
//   {
//     id: "6",
//     name: "Trey Beasley",
//     email: "Trey.beasley@hi5mg.com",
//     subject: "hi trey testing from analysis contact us",
//     message: "hi trey testing from analysis contact us",
//     sentReply: false,
//     dateCreated: "2023-09-23T16:10:55.678Z",
//   },
// ];

export function UserQueries() {
  const [queries, setQueries] = useState<UserQuery[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredQueries, setFilteredQueries] =
    useState<UserQuery[]>([]);
  const [selectedQuery, setSelectedQuery] = useState<UserQuery | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replySubject, setReplySubject] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");

  // Simulate API call to fetch queries
  useEffect(() => {
    const fetchQueries = async () => {
      setIsLoading(true);
      try {
        const response: ApiResponse = await queriesRelatedApi.getAllQueries();

        if (response.success) {
          setQueries(response.data.queries || []);
          setFilteredQueries(response.data.queries || []);
        } else {
          throw new Error(response.message || "Failed to load user queries");
        }

        // Queries are already set in state with default values matching the image
      } catch (error) {
        console.error("Failed to fetch Queries:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQueries();
  }, []);

  // Apply filters
  const applyFilters = () => {
    let filtered = queries;

    if (filterName.trim()) {
      filtered = filtered.filter((query) =>
        query.name.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    if (filterEmail.trim()) {
      filtered = filtered.filter((query) =>
        query.email.toLowerCase().includes(filterEmail.toLowerCase())
      );
    }

    setFilteredQueries(filtered);
  };

  // Clear filters
  const clearFilters = () => {
    setFilterName("");
    setFilterEmail("");
    setFilteredQueries(queries);
    setShowFilters(false);
  };

  // Handle reply submission
  const handleSendReply = async () => {
    if (!selectedQuery || !replySubject.trim() || !replyMessage.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      // Simulate API call
      const replyData = {
        id: selectedQuery.id,
        reply: replyMessage
      }
      const response: ApiResponse = await queriesRelatedApi.sendReply(replyData);

      if (response.success) {
        // Update query status
        const updatedQueries = queries.map((q) =>
          q.id === selectedQuery.id ? { ...q, sentReply: true } : q
        );
        setFilteredQueries(
          updatedQueries.filter((q) => {
            let matches = true;
            if (filterName.trim()) {
              matches =
                matches &&
                q.name.toLowerCase().includes(filterName.toLowerCase());
            }
            if (filterEmail.trim()) {
              matches =
                matches &&
                q.email.toLowerCase().includes(filterEmail.toLowerCase());
            }
            return matches;
          })
        );

        if (selectedQuery) {
          setSelectedQuery({ ...selectedQuery, sentReply: true });
        }

        toast.success("Reply sent successfully!");
        setReplyModalOpen(false);
        setReplySubject("");
        setReplyMessage("");
      } else {
        throw new Error(response.message || "Failed to send reply");
      }
    } catch (error) {
      toast.error(error.message || "Failed to send reply. Please try again.");
    }
  };

  // Handle file download
  const handleDownload = (attachment: QueryAttachment) => {
    // In a real application, this would trigger an actual download
    toast.success(`Downloading ${attachment.name}...`);

    // Simulate download
    const link = document.createElement("a");
    link.href = attachment.url;
    link.download = attachment.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get file icon based on type
  const getFileIcon = (type: string) => {
    switch (type) {
      case "image":
        return <ImageIcon className="h-8 w-8 text-blue-500" />;
      case "document":
        return <FileText className="h-8 w-8 text-red-500" />;
      default:
        return <File className="h-8 w-8 text-gray-500" />;
    }
  };

  // Open reply modal
  const openReplyModal = (query: UserQuery) => {
    setSelectedQuery(query);
    setReplySubject(`Re: ${query.subject}`);
    setReplyMessage("");
    setReplyModalOpen(true);
  };

  if (selectedQuery && !replyModalOpen) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedQuery(null)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to List
          </Button>
          <div>
            <img
              src="https://admin.coachable-app.com/images/logo.jpeg"
              alt="Coachable Logo"
              className="h-8 w-auto"
            />
          </div>
        </div>

        {/* Query Details */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-2xl">Query Details</CardTitle>
              <CardDescription>
                User feedback and support request information
              </CardDescription>
            </div>
            <Button
              onClick={() => openReplyModal(selectedQuery)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <Send className="h-4 w-4 mr-2" />
              SEND REPLY
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">
                  NAME
                </Label>
                <p className="mt-1">{selectedQuery.name}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">
                  EMAIL
                </Label>
                <p className="mt-1">{selectedQuery.email}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">
                  SUBJECT
                </Label>
                <p className="mt-1">{selectedQuery.subject}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">
                  SENT REPLY
                </Label>
                <div className="mt-1">
                  <Badge
                    variant={selectedQuery.sentReply ? "default" : "secondary"}
                  >
                    {selectedQuery.sentReply ? "Yes" : "No"}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Message */}
            <div>
              <Label className="text-sm font-medium text-muted-foreground">
                MESSAGE
              </Label>
              <div
                className="mt-1 p-4 bg-muted rounded-lg"
                dangerouslySetInnerHTML={{ __html: selectedQuery.message }}
              />
            </div>

            {/* Date Created */}
            <div>
              <Label className="text-sm font-medium text-muted-foreground">
                DATE CREATED
              </Label>
              <p className="mt-1">
                {new Date(selectedQuery.dateCreated).toLocaleString()}
              </p>
            </div>

            {/* File Attachments */}
            {selectedQuery.attachments &&
              selectedQuery.attachments.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">
                    FILE ATTACHMENT
                  </Label>
                  <div className="mt-4 space-y-3">
                    {selectedQuery.attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between p-4 bg-muted rounded-lg border"
                      >
                        <div className="flex items-center gap-3">
                          {getFileIcon(attachment.type)}
                          <div>
                            <p className="font-medium">{attachment.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {attachment.size}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(attachment)}
                          className="flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img
            src="https://admin.coachable-app.com/images/logo.jpeg"
            alt="Coachable Logo"
            className="h-8 w-auto"
          />
          <div>
            <h1 className="text-3xl font-bold">User Queries</h1>
            <p className="text-muted-foreground">
              Manage user feedback and support requests
            </p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              List
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                {filteredQueries.length}
              </Badge>
            </CardTitle>
            <CardDescription>
              All user queries and feedback submissions
            </CardDescription>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            Filter
          </Button>
        </CardHeader>

        {/* Filter Panel */}
        {showFilters && (
          <CardContent className="border-t bg-muted/50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4">
              <div>
                <Label htmlFor="filter-name">Filter by Name</Label>
                <Input
                  id="filter-name"
                  placeholder="Search by name..."
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="filter-email">Filter by Email</Label>
                <Input
                  id="filter-email"
                  placeholder="Search by email..."
                  value={filterEmail}
                  onChange={(e) => setFilterEmail(e.target.value)}
                />
              </div>
              <div className="flex items-end gap-2">
                <Button onClick={applyFilters} className="flex-1">
                  Apply Filters
                </Button>
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="px-3"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        )}

        <CardContent>
          {/* Queries Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[150px]">Name</TableHead>
                  <TableHead className="w-[200px]">Email</TableHead>
                  <TableHead className="w-[150px]">Subject</TableHead>
                  <TableHead className="flex-1">Message</TableHead>
                  <TableHead className="w-[100px]">Sent Reply</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredQueries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Search className="h-8 w-8" />
                        <p>No queries found matching your filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredQueries.map((query) => (
                    <TableRow
                      key={query.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedQuery(query)}
                    >
                      <TableCell className="font-medium">
                        {query.name}
                      </TableCell>
                      <TableCell>{query.email}</TableCell>
                      <TableCell>{query.subject}</TableCell>
                      <TableCell className="max-w-[300px]">
                        <div
                          className="truncate"
                          dangerouslySetInnerHTML={{ __html: query.message }}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={query.sentReply ? "default" : "secondary"}
                        >
                          {query.sentReply ? "Yes" : "No"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => setSelectedQuery(query)}
                            >
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openReplyModal(query)}
                            >
                              <Mail className="h-4 w-4 mr-2" />
                              Send Reply
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Reply Modal */}
      <Dialog open={replyModalOpen} onOpenChange={setReplyModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Send Reply
            </DialogTitle>
            <DialogDescription>
              Send an email reply to {selectedQuery?.name} (
              {selectedQuery?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reply-subject">Subject</Label>
              <Input
                id="reply-subject"
                value={replySubject}
                onChange={(e) => setReplySubject(e.target.value)}
                placeholder="Enter reply subject..."
              />
            </div>
            <div>
              <Label htmlFor="reply-message">Message</Label>
              <Textarea
                id="reply-message"
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Enter your reply message..."
                rows={10}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setReplyModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendReply}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Send className="h-4 w-4 mr-2" />
                Send Reply
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
